'use client';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { ArrowLeft, ShieldCheck, Clock, AlertTriangle } from 'lucide-react';
import type { AuthState } from './AuthPage';
import { useLanguage } from '@/contexts/LanguageContext';
import { checkRateLimit, resetRateLimit, OTP_EXPIRY_SECONDS } from '@/lib/security';

interface OTPVerifyFormProps {
  authState: AuthState;
  setAuthState: React.Dispatch<React.SetStateAction<AuthState>>;
}

const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;
const DEMO_OTP = '123456';
const RATE_LIMIT_KEY = 'otp-verify';

export default function OTPVerifyForm({ authState, setAuthState }: OTPVerifyFormProps) {
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [countdown, setCountdown] = useState(RESEND_SECONDS);
  const [otpExpiry, setOtpExpiry] = useState(OTP_EXPIRY_SECONDS);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [isExpired, setIsExpired] = useState(false);
  const [isLockedOut, setIsLockedOut] = useState(false);
  const [lockoutSeconds, setLockoutSeconds] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const router = useRouter();
  const { t } = useLanguage();

  // Resend cooldown
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  // OTP expiry countdown
  useEffect(() => {
    if (isExpired || otpExpiry <= 0) return;
    const timer = setInterval(() => {
      setOtpExpiry((s) => {
        if (s <= 1) {
          setIsExpired(true);
          setError('Your verification code has expired. Please request a new one.');
          setDigits(Array(OTP_LENGTH).fill(''));
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isExpired, otpExpiry]);

  // Lockout countdown
  useEffect(() => {
    if (!isLockedOut || lockoutSeconds <= 0) return;
    const timer = setInterval(() => {
      setLockoutSeconds((s) => {
        if (s <= 1) {
          setIsLockedOut(false);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isLockedOut, lockoutSeconds]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleVerify = useCallback(
    async (code: string) => {
      if (isExpired) {
        setError('Your verification code has expired. Please request a new one.');
        return;
      }

      // Rate limit check
      const rl = checkRateLimit(RATE_LIMIT_KEY, 5, 5 * 60 * 1000, 15 * 60 * 1000);
      if (!rl.allowed) {
        const secs = rl.lockedUntilMs ? Math.ceil((rl.lockedUntilMs - Date.now()) / 1000) : 900;
        setIsLockedOut(true);
        setLockoutSeconds(secs);
        setError(`Too many failed attempts. Try again in ${Math.ceil(secs / 60)} minute(s).`);
        setDigits(Array(OTP_LENGTH).fill(''));
        return;
      }

      setIsVerifying(true);
      setError('');
      await new Promise((r) => setTimeout(r, 1000));

      if (code === DEMO_OTP) {
        resetRateLimit(RATE_LIMIT_KEY);
        toast.success('Verified! Welcome to sifibank.');
        if (authState.role === 'customer') {
          router.push('/customer-dashboard');
        } else {
          router.push('/customer-dashboard');
        }
      } else {
        setError(t.auth.invalidCode);
        setDigits(Array(OTP_LENGTH).fill(''));
        inputRefs.current[0]?.focus();
        setIsVerifying(false);
      }
    },
    [authState.role, router, t, isExpired]
  );

  const handleDigitChange = (index: number, value: string) => {
    if (isLockedOut || isExpired) return;
    const cleaned = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[index] = cleaned;
    setDigits(newDigits);
    setError('');

    if (cleaned && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newDigits.every((d) => d !== '') && newDigits.join('').length === OTP_LENGTH) {
      handleVerify(newDigits.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    if (isLockedOut || isExpired) return;
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (pasted.length === OTP_LENGTH) {
      const newDigits = pasted.split('');
      setDigits(newDigits);
      handleVerify(pasted);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    setCountdown(RESEND_SECONDS);
    setOtpExpiry(OTP_EXPIRY_SECONDS);
    setIsExpired(false);
    setIsLockedOut(false);
    setLockoutSeconds(0);
    setDigits(Array(OTP_LENGTH).fill(''));
    setError('');
    toast.info('New verification code sent.');
    inputRefs.current[0]?.focus();
  };

  const formatPhone = (phone: string) => {
    return phone.replace(/(\+\d{2})(\d{4})(\d{3})(\d{3})/, '$1 $2 $3 $4');
  };

  const expiryMinutes = Math.floor(otpExpiry / 60);
  const expirySeconds = otpExpiry % 60;
  const expiryWarning = otpExpiry <= 60 && !isExpired;

  return (
    <div className="fade-in">
      {/* Back */}
      <button
        onClick={() => setAuthState((prev) => ({ ...prev, view: 'login' }))}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
      >
        <ArrowLeft size={15} />
        {t.auth.backToSignIn}
      </button>

      {/* Icon */}
      <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
        <ShieldCheck size={24} className="text-primary" />
      </div>

      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-1.5">{t.auth.verifyNumber}</h2>
        <p className="text-sm text-muted-foreground">
          {t.auth.sentCode}{' '}
          <span className="text-foreground font-medium font-mono-data">
            {formatPhone(authState.phone)}
          </span>
        </p>
      </div>

      {/* OTP expiry indicator */}
      {!isExpired && (
        <div className={`flex items-center gap-2 mb-4 text-xs px-3 py-2 rounded-lg border ${
          expiryWarning
            ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' :'bg-muted border-border text-muted-foreground'
        }`}>
          <Clock size={12} className={expiryWarning ? 'text-amber-400' : ''} />
          <span>
            Code expires in{' '}
            <span className="font-mono-data font-semibold tabular-nums">
              {expiryMinutes}:{String(expirySeconds).padStart(2, '0')}
            </span>
          </span>
        </div>
      )}

      {/* Lockout warning */}
      {isLockedOut && (
        <div className="flex items-start gap-2 mb-4 text-xs px-3 py-2.5 rounded-lg border bg-red-500/10 border-red-500/30 text-red-400">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>
            Account temporarily locked due to too many failed attempts. Try again in{' '}
            <span className="font-mono-data font-semibold tabular-nums">
              {Math.floor(lockoutSeconds / 60)}:{String(lockoutSeconds % 60).padStart(2, '0')}
            </span>
          </span>
        </div>
      )}

      {/* OTP inputs */}
      <div className="flex gap-2.5 mb-5" onPaste={handlePaste}>
        {digits.map((digit, index) => (
          <input
            key={`otp-digit-${index}`}
            ref={(el) => { inputRefs.current[index] = el; }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleDigitChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            className={`otp-input ${digit ? 'filled' : ''} ${isExpired || isLockedOut ? 'opacity-50 cursor-not-allowed' : ''}`}
            aria-label={`OTP digit ${index + 1}`}
            disabled={isVerifying || isExpired || isLockedOut}
            autoComplete="one-time-code"
          />
        ))}
      </div>

      {/* Error */}
      {error && (
        <p className="text-xs text-negative mb-4 bg-negative/8 border border-negative/20 rounded-lg px-3 py-2.5">
          {error}
        </p>
      )}

      {/* Verify button */}
      <button
        type="button"
        disabled={digits.some((d) => !d) || isVerifying || isExpired || isLockedOut}
        onClick={() => handleVerify(digits.join(''))}
        className="btn-primary w-full mb-5"
        style={{ minHeight: '48px' }}
      >
        {isVerifying ? (
          <span className="flex items-center gap-2">
            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {t.auth.verifying}
          </span>
        ) : (
          t.auth.verifyAndContinue
        )}
      </button>

      {/* Resend */}
      <p className="text-sm text-center text-muted-foreground">
        {t.auth.didntReceive}{' '}
        {countdown > 0 ? (
          <span className="text-foreground font-medium tabular-nums">
            {t.auth.resendIn} {countdown}s
          </span>
        ) : (
          <button
            onClick={handleResend}
            className="text-primary font-semibold hover:underline"
          >
            {t.auth.resendCode}
          </button>
        )}
      </p>

      {/* Demo hint */}
      <div className="mt-8 p-3.5 rounded-xl bg-muted border border-border text-center">
        <p className="text-xs text-muted-foreground">
          {t.auth.demoOtpHint}{' '}
          <span className="font-mono-data text-primary font-bold text-sm tracking-[0.3em]">
            123456
          </span>
        </p>
      </div>
    </div>
  );
}